// Auto-layout: BFS from rootId. Depth = y, sibling index = x.
// Unreachable-from-root nodes get appended at depth 0 to the right.

import type { Graph, FlowNode } from "./types";
import { BLOCK_W, BLOCK_H } from "./types";

const X_GAP = 60;
const Y_GAP = 40;

function targetsOf(n: FlowNode): string[] {
  switch (n.kind) {
    case "start":
    case "play":
    case "collect":
      return n.next ? [n.next] : [];
    case "menu":
      return [
        ...Object.values(n.branches).filter(Boolean),
        n.onTimeout,
        n.onInvalid,
      ].filter(Boolean);
    case "ring":
      return [n.onAnswer, n.onNoAnswer, n.onNoOneOnline].filter(Boolean);
    case "voicemail":
    case "hangup":
      return [];
  }
}

export function autoLayout(
  graphRoot: string,
  nodes: Record<string, FlowNode>,
): Record<string, { x: number; y: number }> {
  const seen = new Set<string>();
  const byDepth: Record<number, string[]> = {};
  const queue: { id: string; depth: number }[] = [{ id: graphRoot, depth: 0 }];
  while (queue.length > 0) {
    const { id, depth } = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    (byDepth[depth] ??= []).push(id);
    const node = nodes[id];
    if (!node) continue;
    for (const t of targetsOf(node)) queue.push({ id: t, depth: depth + 1 });
  }
  for (const id of Object.keys(nodes)) {
    if (!seen.has(id)) (byDepth[0] ??= []).push(id);
  }

  // Centre each row horizontally — wider rows extend symmetrically
  // so the root sits roughly above whatever's below it.
  const out: Record<string, { x: number; y: number }> = {};
  const rowWidthOf = (n: number): number => n * BLOCK_W + Math.max(0, n - 1) * X_GAP;
  const widest = Math.max(...Object.values(byDepth).map((ids) => rowWidthOf(ids.length)));
  for (const [d, ids] of Object.entries(byDepth)) {
    const depth = parseInt(d, 10);
    const rowW = rowWidthOf(ids.length);
    const offset = (widest - rowW) / 2;
    ids.forEach((id, i) => {
      out[id] = {
        x: offset + i * (BLOCK_W + X_GAP),
        y: 40 + depth * (BLOCK_H + Y_GAP),
      };
    });
  }
  return out;
}

export function ensurePositions(graph: Graph): Graph {
  const missing = Object.keys(graph.nodes).some((id) => !graph.positions[id]);
  if (!missing && Object.keys(graph.positions).length > 0) return graph;
  return { ...graph, positions: autoLayout(graph.rootId, graph.nodes) };
}

/** Assign display numbers to nodes missing one. Existing numbers are
 *  never changed (and stale entries for deleted nodes are kept by the
 *  editor, so numbers are never reused). New numbers follow BFS order
 *  from the root — the natural reading order of the flow — starting
 *  after the highest number ever assigned. */
export function ensureSeqs(graph: Graph): Graph {
  const seqs = graph.seqs ?? {};
  const missing = Object.keys(graph.nodes).filter((id) => seqs[id] == null);
  if (missing.length === 0) return { ...graph, seqs };

  // BFS order first, then whatever is unreachable (alphabetical for
  // determinism).
  const order: string[] = [];
  const seen = new Set<string>();
  const queue = [graph.rootId];
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    if (graph.nodes[id]) order.push(id);
    const node = graph.nodes[id];
    if (node) for (const t of targetsOf(node)) queue.push(t);
  }
  for (const id of Object.keys(graph.nodes).sort()) {
    if (!seen.has(id)) order.push(id);
  }

  let next = Math.max(0, ...Object.values(seqs)) + 1;
  const out = { ...seqs };
  for (const id of order) {
    if (out[id] == null) out[id] = next++;
  }
  return { ...graph, seqs: out };
}

/** Mirror of the control-plane's flowIsAnswerable (render-flow.ts).
 *  A flow "answers" if a node reachable from root is play/collect/menu/
 *  ring/voicemail; a graph that only reaches hangup never picks up and
 *  would take the number off the air. Kept in sync with the server;
 *  used to warn in the editor and disable mute flows in the activation
 *  / fallback selects. */
const ANSWERING_KINDS = new Set(["play", "collect", "menu", "ring", "voicemail"]);
export function flowIsAnswerable(
  graph: { rootId?: string; nodes?: Record<string, FlowNode> } | null | undefined,
): boolean {
  if (!graph?.nodes || !graph.rootId) return false;
  const seen = new Set<string>();
  const queue = [graph.rootId];
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const n = graph.nodes[id];
    if (!n) continue;
    if (ANSWERING_KINDS.has(n.kind)) return true;
    for (const t of targetsOf(n)) queue.push(t);
  }
  return false;
}

export function boundingBox(positions: Record<string, { x: number; y: number }>): {
  minX: number; minY: number; maxX: number; maxY: number;
} {
  const xs = Object.values(positions).map((p) => p.x);
  const ys = Object.values(positions).map((p) => p.y);
  if (xs.length === 0) return { minX: 0, minY: 0, maxX: BLOCK_W, maxY: BLOCK_H };
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs) + BLOCK_W,
    maxY: Math.max(...ys) + BLOCK_H,
  };
}
